import React, { useState, useEffect, useCallback, useContext, useMemo } from 'react';
import withAuth from '../../common/auth';
import { ToastContainer, toast } from 'react-toastify';
import { Slide } from 'react-toastify';
import { FiRefreshCcw, FiDownloadCloud, FiSearch, FiBarChart2, FiX } from 'react-icons/fi';
import { ReportEmbedContext } from '../reportEmbedContext';
import { GET_INVENTORY_DEAD_STOCK, BASE_URL } from '../../utlilities/config';
import { getWebUserInfo, initializeWebUserInfo } from '../../utlilities/webConfig';
import axios from 'axios';
import * as XLSX from 'xlsx';

const isElectron = () =>
    typeof window !== 'undefined' && window.process && window.process.type;

const getIpcRenderer = () => {
    if (!isElectron()) return null;
    return window.require('electron').ipcRenderer;
};

const pickPositiveClientId = (...candidates) => {
    for (const raw of candidates) {
        const value = Number(raw);
        if (Number.isFinite(value) && value > 0) return value;
    }
    return null;
};

const resolveClientId = async () => {
    const ipcRenderer = getIpcRenderer();

    if (ipcRenderer) {
        const [generalSettings, clientInfo] = await Promise.all([
            ipcRenderer.invoke('store-get', 'generalsettings'),
            ipcRenderer.invoke('store-get', 'clientInfo'),
        ]);

        return pickPositiveClientId(
            generalSettings?.ClientID,
            generalSettings?.clientID,
            clientInfo?.ClientID,
            clientInfo?.clientID,
            clientInfo?.ID,
            localStorage.getItem('cloud_selected_client_id'),
        );
    }

    initializeWebUserInfo();
    const web = getWebUserInfo();

    return pickPositiveClientId(
        web.generalSettings?.ClientID,
        web.generalSettings?.clientID,
        web.clientInfo?.ClientID,
        web.clientInfo?.clientID,
        web.clientInfo?.ID,
        localStorage.getItem('cloud_selected_client_id'),
    );
};

const Spinner = () => (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-white bg-opacity-80 backdrop-blur-sm">
        <div className="flex flex-col items-center gap-3">
            <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-indigo-600"></div>
            <p className="text-indigo-600 font-medium">Loading...</p>
        </div>
    </div>
);

const DEFAULT_DEAD_DAYS = 60;
const TOP_PER_BUCKET = 40;

const itemColumns = [
    { key: 'itemNo', label: 'Item No' },
    { key: 'itemName', label: 'Item Name' },
    { key: 'daysSinceSale', label: 'Days Since Sale', align: 'right' },
    { key: 'daysSincePurchase', label: 'Days Since Purchase', align: 'right' },
    { key: 'onhand', label: 'On Hand', align: 'right' },
    { key: 'stockValueCost', label: 'Stock Value', align: 'right' },
    { key: 'lastSaleDate', label: 'Last Sale' },
    { key: 'oldestPurchaseDate', label: 'Oldest Purchase' },
    { key: 'supplierName', label: 'Supplier' },
];

const formatDateString = (value) => {
    if (!value) return '-';
    try {
        return new Date(value).toLocaleDateString('en-GB', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
        });
    } catch {
        return value;
    }
};

const formatCurrency = (value) => `₹${parseFloat(value || 0).toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
})}`;

const formatNumber = (value) => {
    if (value == null || value === '') return '-';
    return Number(value).toLocaleString('en-IN');
};

const getBucketLabel = (bucketKey, buckets) => {
    const bucket = (buckets || []).find((b) => b.bucketKey === bucketKey);
    return bucket?.bucketLabel || bucketKey || '-';
};

const BucketCards = ({ buckets, itemsCount, selectedBucket, onSelect, interactive = false }) => {
    if (!buckets.length) return null;

    const cardClass = (isActive) =>
        `rounded-xl border p-3 text-left shadow-sm transition ${
            isActive
                ? 'border-indigo-400 bg-indigo-50 ring-2 ring-indigo-200'
                : 'border-slate-200 bg-white hover:border-indigo-200'
        }`;

    const Wrapper = interactive ? 'button' : 'div';
    const wrapperProps = (bucketKey) =>
        interactive
            ? { type: 'button', onClick: () => onSelect?.(bucketKey) }
            : {};

    return (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Wrapper
                {...wrapperProps('all')}
                className={cardClass(selectedBucket === 'all')}
            >
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">All Buckets</p>
                <p className="mt-1 text-lg font-semibold text-slate-800">{itemsCount}</p>
                <p className="text-xs text-slate-500">items loaded</p>
            </Wrapper>
            {buckets.map((bucket) => (
                <Wrapper
                    key={bucket.bucketKey}
                    {...wrapperProps(bucket.bucketKey)}
                    className={cardClass(selectedBucket === bucket.bucketKey)}
                >
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                        {bucket.bucketLabel}
                    </p>
                    <p className="mt-1 text-lg font-semibold text-slate-800">{bucket.itemCount ?? 0}</p>
                    <p className="text-xs text-slate-500">
                        {formatCurrency(bucket.stockValueCost)} · {bucket.pctOfValue ?? 0}%
                    </p>
                </Wrapper>
            ))}
        </div>
    );
};

const renderItemCell = (column, row) => {
    switch (column.key) {
        case 'stockValueCost':
            return formatCurrency(row.stockValueCost);
        case 'lastSaleDate':
        case 'oldestPurchaseDate':
            return formatDateString(row[column.key]);
        case 'daysSinceSale':
        case 'daysSincePurchase':
        case 'onhand':
            return formatNumber(row[column.key]);
        default:
            return row[column.key] || '-';
    }
};

export const DeadStockReport = () => {
    const embedded = useContext(ReportEmbedContext);
    const token = localStorage.getItem('accessToken');

    const [loading, setLoading] = useState(false);
    const [deadDays, setDeadDays] = useState(DEFAULT_DEAD_DAYS);
    const [searchText, setSearchText] = useState('');
    const [selectedBucket, setSelectedBucket] = useState('all');
    const [summary, setSummary] = useState(null);
    const [isSummaryModalOpen, setIsSummaryModalOpen] = useState(false);
    const [buckets, setBuckets] = useState([]);
    const [items, setItems] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 100;

    const filteredItems = useMemo(() => {
        let rows = items;
        if (selectedBucket !== 'all') {
            rows = rows.filter((row) => row.bucketKey === selectedBucket);
        }
        if (!searchText.trim()) return rows;
        const query = searchText.toLowerCase();
        return rows.filter((row) =>
            String(row.itemNo || '').includes(query) ||
            String(row.itemName || '').toLowerCase().includes(query) ||
            String(row.category || '').toLowerCase().includes(query) ||
            String(row.company || '').toLowerCase().includes(query) ||
            String(row.supplierName || '').toLowerCase().includes(query)
        );
    }, [items, selectedBucket, searchText]);

    const totalPages = Math.max(1, Math.ceil(filteredItems.length / itemsPerPage));
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedItems = filteredItems.slice(startIndex, endIndex);

    useEffect(() => {
        setCurrentPage(1);
    }, [deadDays, selectedBucket, searchText, items.length]);

    const getDeadStockReport = useCallback(async () => {
        const parsedDeadDays = Number(deadDays);
        if (!Number.isFinite(parsedDeadDays) || parsedDeadDays <= 0) {
            toast.error('Dead days must be a positive number', { autoClose: 2000 });
            return;
        }

        setLoading(true);
        try {
            const clientId = await resolveClientId();
            if (!clientId) {
                toast.error('Client ID not found. Please log out and log in again.', { autoClose: 2500 });
                setLoading(false);
                return;
            }

            const response = await axios.get(`${BASE_URL}${GET_INVENTORY_DEAD_STOCK}`, {
                headers: {
                    'XApiKey': process.env.REACT_APP_API_KEY,
                    'Authorization': `Bearer ${token}`,
                    'ClientID': String(clientId),
                },
                params: {
                    deadDays: parsedDeadDays,
                    topPerBucket: TOP_PER_BUCKET,
                },
            });

            if (response.status === 200) {
                const data = response.data.data || {};
                setSummary(data.summary || null);
                setBuckets(data.buckets || []);
                setItems(data.items || []);
                setSelectedBucket('all');
                if (!data.items?.length) {
                    toast.info('No dead stock items found for the selected criteria', { autoClose: 2000 });
                } else {
                    toast.success('Report generated successfully', { autoClose: 1000 });
                }
            } else {
                toast.error(response.data?.message || 'Failed to fetch dead stock report', { autoClose: 2000 });
                setSummary(null);
                setBuckets([]);
                setItems([]);
            }
        } catch (error) {
            console.error('Failed to fetch dead stock report:', error);
            toast.error('Failed to fetch dead stock report', { autoClose: 2000 });
            setSummary(null);
            setBuckets([]);
            setItems([]);
        } finally {
            setLoading(false);
        }
    }, [deadDays, token]);

    const handleExport = () => {
        if (!items.length) {
            toast.error('No data to export', { autoClose: 2000 });
            return;
        }

        try {
            const wb = XLSX.utils.book_new();

            if (summary) {
                const summarySheet = XLSX.utils.json_to_sheet([{
                    'Total Stock Value': Number(summary.totalStockValue || 0),
                    'Total Dead Value': Number(summary.totalDeadValue || 0),
                    'Total Dead Items': Number(summary.totalDeadItems || 0),
                    'Dead %': Number(summary.deadPct || 0),
                    'Never Sold Items': Number(summary.neverSoldItems || 0),
                    'Never Sold Value': Number(summary.neverSoldValue || 0),
                    'Oldest Dead Days': Number(summary.oldestDeadDays || 0),
                    'Dead Days Threshold': Number(deadDays),
                }]);
                XLSX.utils.book_append_sheet(wb, summarySheet, 'Summary');
            }

            const bucketRows = buckets.map((bucket) => ({
                'Bucket': bucket.bucketLabel || bucket.bucketKey,
                'Key': bucket.bucketKey,
                'Min Days': bucket.minDays ?? '',
                'Max Days': bucket.maxDays ?? '',
                'Item Count': Number(bucket.itemCount || 0),
                'Stock Value (Cost)': Number(bucket.stockValueCost || 0),
                '% of Dead Value': Number(bucket.pctOfValue || 0),
            }));
            if (bucketRows.length) {
                XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(bucketRows), 'Buckets');
            }

            const itemRows = items.map((row) => ({
                'Item No': row.itemNo,
                'Item Name': row.itemName || '',
                'Category': row.category || '',
                'Company': row.company || '',
                'Bucket': getBucketLabel(row.bucketKey, buckets),
                'Days Since Sale': row.daysSinceSale ?? '',
                'Days Since Purchase': row.daysSincePurchase ?? '',
                'On Hand': Number(row.onhand || 0),
                'Stock Value (Cost)': Number(row.stockValueCost || 0),
                'Last Sale Date': formatDateString(row.lastSaleDate),
                'Oldest Purchase Date': formatDateString(row.oldestPurchaseDate),
                'Supplier': row.supplierName || '',
            }));
            XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(itemRows), 'Items');

            XLSX.writeFile(wb, `Dead_Stock_Report_${deadDays}_days.xlsx`);
            toast.success(`Exported ${items.length} items`, { autoClose: 2000 });
        } catch (error) {
            console.error('Error exporting dead stock report:', error);
            toast.error('Failed to export report', { autoClose: 2000 });
        }
    };

    return (
        <div className="flex h-full bg-background">
            <ToastContainer
                position="top-center"
                autoClose={1000}
                limit={1}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="light"
                transition={Slide}
            />

            {loading && <Spinner />}

            <div className="flex-1 flex flex-col h-full overflow-hidden">
                <header className="shrink-0 border-b border-slate-200 bg-white px-3 py-2.5 mb-2">
                    <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
                        <div className="min-w-0 flex-1">
                            {!embedded && (
                                <div className="mb-2">
                                    <p className="text-xs font-semibold uppercase tracking-widest text-indigo-500">
                                        Stock Reports
                                    </p>
                                    <h1 className="text-lg font-bold text-slate-800">
                                        Dead Stock Report
                                    </h1>
                                </div>
                            )}
                            <form className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                                <label className="flex flex-col gap-1 text-xs font-medium text-slate-700">
                                    Dead Days
                                    <input
                                        type="number"
                                        min="1"
                                        value={deadDays}
                                        onChange={(e) => setDeadDays(e.target.value)}
                                        className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-indigo-400 focus:ring-2 focus:ring-indigo-200 transition"
                                    />
                                </label>
                                {/* {buckets.length > 0 && (
                                    <label className="flex flex-col gap-1 text-xs font-medium text-slate-700">
                                        Bucket
                                        <select
                                            value={selectedBucket}
                                            onChange={(e) => setSelectedBucket(e.target.value)}
                                            className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-indigo-400 focus:ring-2 focus:ring-indigo-200 transition"
                                        >
                                            <option value="all">All Buckets ({items.length})</option>
                                            {buckets.map((bucket) => (
                                                <option key={bucket.bucketKey} value={bucket.bucketKey}>
                                                    {bucket.bucketLabel} ({bucket.itemCount ?? 0})
                                                </option>
                                            ))}
                                        </select>
                                    </label>
                                )} */}
                                <label className="flex flex-col gap-1 text-xs font-medium text-slate-700">
                                    Search Items
                                    <div className="relative">
                                        <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={14} />
                                        <input
                                            type="text"
                                            value={searchText}
                                            onChange={(e) => setSearchText(e.target.value)}
                                            placeholder="Item name, supplier..."
                                            className="w-full rounded-lg border border-slate-200 bg-white pl-9 pr-3 py-2 text-sm text-slate-700 shadow-sm focus:border-indigo-400 focus:ring-2 focus:ring-indigo-200 transition"
                                        />
                                    </div>
                                </label>
                            </form>
                        </div>
                        <div className="flex flex-wrap items-center justify-start lg:justify-end gap-2 sm:gap-3">
                            <button
                                type="button"
                                onClick={() => setIsSummaryModalOpen(true)}
                                disabled={!summary && !buckets.length}
                                title="View summary metrics"
                                className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-600 shadow-sm transition hover:border-indigo-300 hover:text-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <FiBarChart2 size={14} />
                                Summary
                            </button>
                            <button
                                type="button"
                                onClick={getDeadStockReport}
                                className="inline-flex items-center gap-2 rounded-full bg-indigo-500 px-5 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-600"
                            >
                                <FiRefreshCcw size={16} />
                                Generate
                            </button>
                            <button
                                type="button"
                                onClick={() => {
                                    setDeadDays(DEFAULT_DEAD_DAYS);
                                    setSearchText('');
                                    setSelectedBucket('all');
                                    setSummary(null);
                                    setIsSummaryModalOpen(false);
                                    setBuckets([]);
                                    setItems([]);
                                }}
                                className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-5 py-2 text-sm font-semibold text-slate-600 shadow-sm transition hover:border-indigo-300 hover:text-indigo-500"
                            >
                                Reset
                            </button>
                            <button
                                type="button"
                                onClick={handleExport}
                                disabled={!items.length}
                                className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-5 py-2 text-sm font-semibold text-slate-600 shadow-sm transition hover:border-indigo-300 hover:text-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <FiDownloadCloud size={16} />
                                Export
                            </button>
                        </div>
                    </div>
                </header>

                <main className="flex-1 overflow-hidden flex flex-col">
                    <div className="flex flex-col gap-4 flex-1 min-h-0 px-1">
                        <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm flex flex-col flex-1 min-h-0">
                            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between flex-shrink-0 mb-1">
                                <p className="text-sm text-slate-500">
                                    {items.length > 0 ? (
                                        <>
                                            Showing <span className="font-semibold text-slate-700">{filteredItems.length ? startIndex + 1 : 0}-{Math.min(endIndex, filteredItems.length)}</span> of{' '}
                                            <span className="font-semibold text-slate-700">{filteredItems.length}</span> items
                                            {selectedBucket !== 'all' ? ` in ${getBucketLabel(selectedBucket, buckets)}` : ''}.
                                        </>
                                    ) : (
                                        `Enter dead days (default ${DEFAULT_DEAD_DAYS}) and click Generate.`
                                    )}
                                </p>
                                {totalPages > 1 && (
                                    <div className="flex items-center gap-2">
                                        <button
                                            onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                                            disabled={currentPage === 1}
                                            className="px-3 py-1.5 rounded-lg border border-slate-200 bg-white text-sm font-medium text-slate-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-indigo-50 hover:border-indigo-300 transition-colors"
                                        >
                                            Previous
                                        </button>
                                        <span className="text-sm text-slate-600">Page {currentPage} of {totalPages}</span>
                                        <button
                                            onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                                            disabled={currentPage === totalPages}
                                            className="px-3 py-1.5 rounded-lg border border-slate-200 bg-white text-sm font-medium text-slate-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-indigo-50 hover:border-indigo-300 transition-colors"
                                        >
                                            Next
                                        </button>
                                    </div>
                                )}
                            </div>

                            <div className="mt-2 flex-1 overflow-auto min-h-0 rounded-xl border border-slate-200">
                                <table className="min-w-full w-full divide-y divide-slate-200 text-sm text-slate-700 border-collapse table-fixed">
                                    <colgroup>
                                        <col className="w-[8%]" />
                                        <col className="w-[22%]" />
                                        <col className="w-[9%]" />
                                        <col className="w-[10%]" />
                                        <col className="w-[8%]" />
                                        <col className="w-[11%]" />
                                        <col className="w-[10%]" />
                                        <col className="w-[10%]" />
                                        <col />
                                    </colgroup>
                                    <thead className="bg-gray-200 sticky top-0 z-10">
                                        <tr>
                                            {itemColumns.map((column) => (
                                                <th
                                                    key={column.key}
                                                    className={`px-3 py-3 text-xs font-semibold uppercase tracking-wide text-slate-500 ${
                                                        column.align === 'right' ? 'text-right' : 'text-left'
                                                    }`}
                                                >
                                                    {column.label}
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100 bg-white">
                                        {paginatedItems.length > 0 ? (
                                            paginatedItems.map((row, index) => (
                                                <tr key={`${row.itemNo}-${index}`} className="hover:bg-indigo-50/40 transition">
                                                    {itemColumns.map((column) => (
                                                        <td
                                                            key={column.key}
                                                            className={`px-3 py-3 ${
                                                                column.align === 'right' ? 'text-right whitespace-nowrap' : 'text-left'
                                                            } ${column.key === 'itemName' || column.key === 'supplierName' ? '' : 'whitespace-nowrap'}`}
                                                        >
                                                            {renderItemCell(column, row)}
                                                        </td>
                                                    ))}
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan={itemColumns.length} className="px-4 py-8 text-center text-slate-500">
                                                    {items.length ? 'No items match the current filter' : 'No dead stock data loaded'}
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </section>
                    </div>
                </main>
            </div>

            {isSummaryModalOpen && (summary || buckets.length > 0) && (
                <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-900/50 p-4">
                    <div className="w-full max-w-5xl max-h-[90vh] overflow-auto rounded-2xl border border-slate-200 bg-white p-4 shadow-xl md:p-6">
                        <div className="flex items-center justify-between gap-3 border-b border-slate-200 pb-3 sticky top-0 bg-white z-10">
                            <h2 className="text-lg font-semibold text-slate-800">Dead Stock Summary</h2>
                            <button
                                type="button"
                                onClick={() => setIsSummaryModalOpen(false)}
                                className="inline-flex items-center justify-center rounded-md p-1 text-slate-500 transition hover:bg-slate-100 hover:text-slate-700"
                                aria-label="Close summary"
                            >
                                <FiX size={20} />
                            </button>
                        </div>

                        {summary && (
                            <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                                <div className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
                                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Total Stock Value</p>
                                    <p className="mt-1 text-base font-semibold text-slate-800">{formatCurrency(summary.totalStockValue)}</p>
                                </div>
                                <div className="rounded-xl border border-red-100 bg-red-50/60 p-3 shadow-sm">
                                    <p className="text-xs font-semibold uppercase tracking-wide text-red-500">Dead Value</p>
                                    <p className="mt-1 text-base font-semibold text-red-700">{formatCurrency(summary.totalDeadValue)}</p>
                                </div>
                                <div className="rounded-xl border border-red-100 bg-red-50/60 p-3 shadow-sm">
                                    <p className="text-xs font-semibold uppercase tracking-wide text-red-500">Dead Items</p>
                                    <p className="mt-1 text-base font-semibold text-red-700">{formatNumber(summary.totalDeadItems)}</p>
                                </div>
                                <div className="rounded-xl border border-amber-100 bg-amber-50/60 p-3 shadow-sm">
                                    <p className="text-xs font-semibold uppercase tracking-wide text-amber-600">Dead %</p>
                                    <p className="mt-1 text-base font-semibold text-amber-700">{summary.deadPct ?? 0}%</p>
                                </div>
                                <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 shadow-sm">
                                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Never Sold</p>
                                    <p className="mt-1 text-base font-semibold text-slate-800">{formatNumber(summary.neverSoldItems)}</p>
                                </div>
                                <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 shadow-sm">
                                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Never Sold Value</p>
                                    <p className="mt-1 text-base font-semibold text-slate-800">{formatCurrency(summary.neverSoldValue)}</p>
                                </div>
                                <div className="rounded-xl border border-indigo-100 bg-indigo-50/60 p-3 shadow-sm">
                                    <p className="text-xs font-semibold uppercase tracking-wide text-indigo-500">Oldest Dead Days</p>
                                    <p className="mt-1 text-base font-semibold text-indigo-700">{formatNumber(summary.oldestDeadDays)}</p>
                                </div>
                            </div>
                        )}

                        {buckets.length > 0 && (
                            <div className="mt-6">
                                <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-500">
                                    Bucket Breakdown
                                </p>
                                <BucketCards
                                    buckets={buckets}
                                    itemsCount={items.length}
                                    selectedBucket={selectedBucket}
                                    onSelect={(bucketKey) => {
                                        setSelectedBucket(bucketKey);
                                        setIsSummaryModalOpen(false);
                                    }}
                                    interactive
                                />
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default withAuth(DeadStockReport);
